import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import useAdInitializer from './AdInitializer';
import { safeLocalStorage } from './AdUtils';

interface DesktopAdProps {
  className?: string;
  adUnitId?: string;
  adSize?: '300x600' | '300x250' | '160x600';
}

export const DesktopAd: React.FC<DesktopAdProps> = ({ 
  className = '',
  adUnitId = 'ca-pub-1421631996968165',
  adSize = '300x600'
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isClosed, setIsClosed] = useState(false);
  const adRef = useRef<HTMLDivElement>(null);
  const { initializeAd } = useAdInitializer();

  const handleClose = () => {
    setIsClosed(true);
    setIsVisible(false);
    // Store user preference to not show ads
    safeLocalStorage.setItem('adsClosed', 'true');
  };

  // Check if user previously closed ads
  useEffect(() => {
    const adsClosed = safeLocalStorage.getItem('adsClosed');
    if (adsClosed === 'true') {
      setIsClosed(true);
      setIsVisible(false);
    }
  }, []);

  // Don't render if user closed ads
  if (isClosed || safeLocalStorage.getItem('adsClosed') === 'true') {
    return null;
  }

  // Initialize ad when component mounts
  useEffect(() => {
    if (adRef.current && !isClosed) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        initializeAd(adRef.current, '3802558024', adUnitId);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [initializeAd, adUnitId, isClosed]);

  if (!isVisible) return null;

  const getAdDimensions = () => {
    switch (adSize) {
      case '300x600':
        return { width: '300px', height: '600px' };
      case '300x250':
        return { width: '300px', height: '250px' };
      case '160x600':
        return { width: '160px', height: '600px' };
      default:
        return { width: '300px', height: '600px' };
    }
  };

  const dimensions = getAdDimensions();

  return (
    <div className={`desktop-ad-container ${className}`}>
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-lg">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-2 right-2 z-10 w-6 h-6 bg-gray-500 hover:bg-gray-600 text-white rounded-full flex items-center justify-center transition-colors duration-200"
          aria-label="Close ad"
        >
          <X className="w-4 h-4" />
        </button>

        {/* AdSense Ad Unit */}
        <div 
          ref={adRef}
          className="ad-unit"
          style={{
            width: dimensions.width,
            height: dimensions.height,
            minWidth: dimensions.width,
            minHeight: dimensions.height
          }}
        >
          <ins 
            className="adsbygoogle"
            style={{ 
              display: 'block',
              width: dimensions.width,
              height: dimensions.height
            }}
            data-ad-client={adUnitId}
            data-ad-slot="3802558024" // Real AdSense slot ID for Desktop Sidebar Ad
            data-ad-format="auto"
            data-full-width-responsive="true"
          />
        </div>

      </div>
    </div>
  );
};

export default DesktopAd;
